import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageBanner from "../components/PageBanner";
import SectionIntro from "../components/SectionIntro";
import api from "../lib/api";
import SEO from "../lib/SEO";

export default function Gallery() {
    const [categories, setCategories] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/categories")
            .then((r) => setCategories(Array.isArray(r.data) ? r.data : []))
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, []);

    return (
        <main data-testid="gallery-page">
            <SEO
                title="Gallery — Cake Central"
                description="Browse our custom cake gallery by occasion — wedding, birthday, baby shower, engagement, anniversary and more. Enquire for a bespoke quote."
                path="/gallery"
            />
            <PageBanner
                eyebrow="Browse by occasion"
                title="Our Cake Gallery"
                image="https://images.unsplash.com/photo-1629576690723-bb3a22ae7126?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            />

            <section className="bg-[#FFF7FB] py-10 md:py-14">
                <div className="px-4 sm:px-6 lg:px-10">
                    <SectionIntro className="mb-10">
                        Each creation is <em>bespoke</em>, made to order for your occasion — explore the
                        portfolio, then share your vision and we'll craft something <em>unforgettable</em>.
                    </SectionIntro>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 max-w-screen-2xl mx-auto">
                        {categories.map((c, i) => (
                            <Link
                                key={c.id}
                                to={`/gallery/${c.slug}`}
                                className="group block relative overflow-hidden card-lift"
                                data-testid={`gallery-category-${i}`}
                            >
                                <div className="aspect-[4/5] overflow-hidden bg-blush-pale">
                                    {c.cover_image_url && (
                                        <img
                                            src={c.cover_image_url}
                                            alt={c.name}
                                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                                            loading="lazy"
                                        />
                                    )}
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                                    <p className="text-[10px] tracking-[0.4em] uppercase opacity-85 mb-2">Portfolio</p>
                                    <h3 className="font-display text-3xl md:text-4xl leading-tight">{c.name}</h3>
                                    {c.description && (
                                        <p className="text-sm text-white/85 mt-2 line-clamp-2 max-w-sm">{c.description}</p>
                                    )}
                                    <span className="inline-flex items-center gap-2 mt-4 text-[10px] tracking-[0.3em] uppercase text-blush-pale opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                                        View portfolio <ArrowRight size={12} />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {loaded && categories.length === 0 && (
                        <p className="text-center text-ink-soft py-20">
                            Our gallery is being updated — please check back soon.
                        </p>
                    )}
                </div>
            </section>
        </main>
    );
}
